import React, { useState, createContext, useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import { theme } from 'utils/styles';
import merge from 'lodash/merge';
import { DROP } from '../common';
import { DROP_CONFIG } from './dropConfig';

interface DropConfig {
  contractAddress: string;
  theme: {
    primary: string;
    background: string;
    copy: string;
    highlight: string;
    activeLink: string;
  };
}
interface DropsContextValue {
  selectedDrop: DROP;
  setSelectedDrop: any;
  dropConfig: DropConfig;
}

const DropsContext = createContext<DropsContextValue>({
  selectedDrop: DROP.genesis,
  setSelectedDrop: () => {},
  dropConfig: DROP_CONFIG[DROP.genesis],
});

interface DropsContextProps {
  drop: DROP;
  children: any;
}

const DropsContextProvider = ({ drop, children }: DropsContextProps) => {
  const [selectedDrop, setSelectedDrop] = useState<DROP>(drop);

  const contextValue = useMemo(
    () => ({
      selectedDrop,
      setSelectedDrop,
      dropConfig: DROP_CONFIG[selectedDrop],
    }),
    [selectedDrop, setSelectedDrop]
  );

  const dropTheme = useMemo(() => {
    const newTheme = merge({}, theme, {
      colors: {
        ...theme.colors,
        ...DROP_CONFIG[selectedDrop].theme,
      }
    });
    return newTheme;
  }, [selectedDrop]);

  return (
    <DropsContext.Provider value={contextValue}>
      <ThemeProvider theme={dropTheme}>{children}</ThemeProvider>
    </DropsContext.Provider>
  );
};

const useDropsContext = () => {
  const context = React.useContext(DropsContext);
  if (context === undefined) {
    throw new Error('useDropsContext must be used within a DropsContextProvider');
  }
  return context;
};

export { DropsContextProvider, useDropsContext };
